import { NgComponentOutlet } from '@angular/common';
import { Component, computed, input, Signal } from '@angular/core';
import { BaseComponent } from '@shared/classes';
import { OutOfStockComponent } from '@shared/components/challenges/elements/out-of-stock/outofstock.component';
import { CampaignPhase, SubType } from '@shared/constants';
import { StepChallenge } from '@shared/factory';
import {
  ChallengeDynamicComponent,
  ExtendedStepChallenge,
  StepChallengeConfig,
} from '@shared/models';

import { Variant1Component } from './variant1/variant1.component';
import { Variant2Component } from './variant2/variant2.component';
import { Variant3Component } from './variant3/variant3.component';
import { Variant4Component } from './variant4/variant4.component';
import { Variant6Component } from './variant6/variant6.component';

@Component({
  selector: 'b2c-step-challenge',
  templateUrl: './step-challenge.component.html',
  styles: `
    .component-container {
      position: relative;
    }
  `,
  imports: [NgComponentOutlet, OutOfStockComponent],
})
export class StepChallengeComponent extends BaseComponent {
  index = input<number>(0);
  withShadow = input<boolean>(true);
  isBisCampaign = input<boolean>(false);
  isEventChallenge = input<boolean>(false);
  challenge = input.required<StepChallenge>();
  loadBannerInPriority = input<boolean>(false);
  phase = input<CampaignPhase>(CampaignPhase.CURRENT);

  /** Forwarded to the variant — see {@link StepBaseClass.previewSelected}. */
  previewSelected = input<boolean>(false);

  // On a retailer that runs bonus (bis) campaigns, sub type designs apply to
  // those campaigns only and the standard ones keep the default design
  // (EAI-2165). A retailer without a bonus campaign has no such split, so its
  // sub type applies to the standard cards it is sent on (EAI-2302).
  protected readonly subType: Signal<SubType> = computed(() =>
    !this.environment.hasCampaignBis || this.isBisCampaign()
      ? this.challenge().subType
      : SubType.DEFAULT
  );

  public config: Signal<StepChallengeConfig> = computed(() => {
    const extendedConfig: ExtendedStepChallenge =
      this.loyaltyChallengeConfig.components.challenges.step;
    return extendedConfig[this.subType()] ?? extendedConfig[SubType.DEFAULT];
  });

  public components: Signal<
    ChallengeDynamicComponent<StepChallengeConfig, StepChallenge>
  > = computed(() => {
    const inputs = {
      phase: this.phase(),
      index: this.index(),
      config: this.config(),
      challenge: this.challenge(),
      withShadow: this.withShadow(),
      isBisCampaign: this.isBisCampaign(),
      isEventChallenge: this.isEventChallenge(),
      loadBannerInPriority: this.loadBannerInPriority(),
      // Only the variants the onboarding deck renders declare this input;
      // forwarding `false` to the others would log NG0303 on every page.
      ...(this.previewSelected() && { previewSelected: true }),
    };
    const variant2Inputs = {
      ...inputs,
    };
    // Only the 4th variant renders a sub type specific design
    const variant4Inputs = { ...inputs, subType: this.subType() };
    return {
      [this.Variants.VARIANT_1]: {
        component: Variant1Component,
        inputs: inputs,
      },
      [this.Variants.VARIANT_2]: {
        component: Variant2Component,
        inputs: variant2Inputs,
      },
      [this.Variants.VARIANT_3]: {
        component: Variant3Component,
        inputs: inputs,
      },
      [this.Variants.VARIANT_4]: {
        component: Variant4Component,
        inputs: variant4Inputs,
      },
      [this.Variants.VARIANT_6]: {
        component: Variant6Component,
        inputs: inputs,
      },
    };
  });
}
